import { useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { campaignTemplatesMap } from '../../templates';
import { CampaignTemplate } from '@/connection/campaigns/fetch-campaign-templates';
import classes from '../create-campaign-flow.module.css';
import { getUserTimezone } from '@ui/calendar/utils';
import QuillInactiveProvider from '@ui/quill/quill-inactive-provider';

type PreviewSize = 'desktop' | 'tablet' | 'mobile';

interface TemplatePreviewProps {
  template: CampaignTemplate;
  previewSize: PreviewSize;
}

export default function TemplatePreview({ template, previewSize }: TemplatePreviewProps) {
  const event = useAppSelector(getWorkingProject);
  const containerRef = useRef<HTMLDivElement>(null);
  const templateRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [scaledSize, setScaledSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const node = templateRef.current;
    const container = containerRef.current;
    if (!node || !container) return;

    node.inert = true;

    const updateScale = () => {
      const { offsetWidth: width, offsetHeight: height } = node;
      if (!width || !height) return;
      const nextScale = container.clientWidth / width;
      setScale(nextScale);
      setScaledSize({ width: width * nextScale, height: height * nextScale });
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    return () => observer.disconnect();
  }, [event]);

  const TemplateComponent = campaignTemplatesMap.get(template.uuid);

  return (
    <div className={classes.previewContainer}>
      <div className={`${classes.browserFrame} ${classes[`browserFrame--${previewSize}`]}`}>
        <div className={classes.browserChrome}>
          <span className={`${classes.trafficDot} ${classes.trafficDotRed}`} />
          <span className={`${classes.trafficDot} ${classes.trafficDotYellow}`} />
          <span className={`${classes.trafficDot} ${classes.trafficDotGreen}`} />
        </div>
        <div className={classes.emailClientLayout}>
          <div className={classes.emailSidebarNarrow}>
            <div className={classes.placeholderBar} style={{ width: '60%' }} />
            <div className={classes.placeholderBar} style={{ width: '40%' }} />
            <div className={classes.placeholderBar} style={{ width: '50%' }} />
            <div className={classes.placeholderBar} style={{ width: '70%' }} />
            <div className={classes.placeholderBar} style={{ width: '45%' }} />
            <div className={classes.placeholderBar} style={{ width: '55%' }} />
            <div className={classes.placeholderBar} style={{ width: '35%' }} />
            <div className={classes.placeholderBar} style={{ width: '65%' }} />
          </div>

          <div className={classes.emailSidebarWide}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={classes.emailListItem}>
                <div className={classes.placeholderDot} />
                <div className={classes.emailListItemLines}>
                  <div className={classes.placeholderBar} style={{ width: '80%' }} />
                  <div className={classes.placeholderBarLight} style={{ width: '60%' }} />
                </div>
              </div>
            ))}
          </div>

          <div className={classes.emailMain}>
            <div className={classes.emailHeader}>
              <div className={classes.emailHeaderRow}>
                <div className={classes.placeholderDot} />
                <div className={classes.emailHeaderLines}>
                  <div className={classes.placeholderBar} style={{ width: '50%' }} />
                  <div className={classes.placeholderBarLight} style={{ width: '30%' }} />
                </div>
              </div>
            </div>

            <div className={classes.emailBody}>
              <div ref={containerRef} style={{ width: '100%' }}>
                {event && TemplateComponent && (
                  <div
                    style={{
                      width: scaledSize.width || undefined,
                      height: scaledSize.height || undefined,
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ transformOrigin: 'top left', transform: `scale(${scale})` }}>
                      <div ref={templateRef} style={{ width: 'max-content' }}>
                        <QuillInactiveProvider>
                          <TemplateComponent event={event} timezone={getUserTimezone()} />
                        </QuillInactiveProvider>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
